type PlacesResult = {
  formatted_address?: string;
  address_components?: Array<{
    types: string[];
    long_name: string;
    short_name: string;
  }>;
};

const componentOf = (
  result: PlacesResult | undefined,
  type: string,
  form: 'long_name' | 'short_name' = 'long_name',
) =>
  result?.address_components?.find((c) => c.types.includes(type))?.[form] || '';

/**
 * Maps a Google Places result onto our stored address parts (ClickUp
 * 86bcgvh8k), e.g. "1889 E Maule Ave Suite J, Las Vegas, NV 89119, USA" ->
 * addressLine1 "1889 E Maule Ave", addressLine2 "Suite J", city "Las Vegas",
 * state "NV", zip "89119", country "USA".
 *
 * Deliberate choices, because these decide what every composed address reads
 * like:
 * - addressLine1 is the street alone (number + route, as Google writes it in
 *   formatted_address). Places with no street number fall back to the first
 *   segment of the formatted address.
 * - addressLine2 is a unit Google put after the street ("Suite J", "#42"),
 *   or empty.
 * - city comes from `locality` ("Las Vegas"), not administrative_area_level_2,
 *   which is the county ("Clark County").
 * - state uses short_name ("NV") rather than long_name ("Nevada").
 * - country is "USA" for the US, matching Google's formatted address, so the
 *   composed address ends the same way.
 */
export const parsePlacesResult = (results: PlacesResult[] = []) => {
  const result = results?.[0];
  const formatted = result?.formatted_address || '';
  const firstSegment = formatted.split(',')[0].trim();

  // route short_name ("E Maule Ave") is how formatted_address spells it.
  const street = [
    componentOf(result, 'street_number'),
    componentOf(result, 'route', 'short_name'),
  ]
    .filter(Boolean)
    .join(' ');
  const hasStreet =
    Boolean(componentOf(result, 'street_number')) && Boolean(street);

  // What follows the street in the first segment is the unit:
  // "1889 E Maule Ave Suite J" -> "Suite J".
  const unit =
    hasStreet && firstSegment.toLowerCase().startsWith(street.toLowerCase())
      ? firstSegment
          .slice(street.length)
          .replace(/^[\s,]+/, '')
          .trim()
      : '';

  const country = componentOf(result, 'country', 'short_name');

  // Some regions have no locality; fall back through the finer-grained
  // administrative levels before giving up.
  const city =
    componentOf(result, 'locality') ||
    componentOf(result, 'postal_town') ||
    componentOf(result, 'sublocality') ||
    componentOf(result, 'administrative_area_level_2');

  return {
    addressLine1: hasStreet ? street : firstSegment,
    addressLine2: unit,
    city,
    state: componentOf(result, 'administrative_area_level_1', 'short_name'),
    zip: componentOf(result, 'postal_code'),
    country: country === 'US' ? 'USA' : country,
  };
};

/**
 * The addressLine2 to keep when a new Places result is picked: the unit
 * Google gave, else the one already entered, unless that is really a copy of
 * a formatted address (old records stored the whole address there too).
 */
export const unitToKeep = (
  parsedUnit: string,
  currentLine2?: string | null,
) => {
  if (parsedUnit) {
    return parsedUnit;
  }

  const current = (currentLine2 || '').trim();
  return current.includes(',') ? '' : current;
};

export default parsePlacesResult;
